/* eslint-disable @typescript-eslint/consistent-type-definitions */
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import { Box, CircularProgress, StyledEngineProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  AppState,
  Auth0Context,
  Auth0ContextInterface,
  Auth0Provider,
  useAuth0,
  User,
} from "@auth0/auth0-react";
import { IconDefinition } from "@fortawesome/pro-light-svg-icons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { APIProvider } from "@vis.gl/react-google-maps";
import * as Highcharts from "highcharts/highstock";
import { DateTime } from "luxon";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import "react-toastify/dist/ReactToastify.css";

import { DialogProvider } from "@utilifeed/components/providers";
import {
  ApiContext,
  APIError,
  BrandedThemeProvider,
  BrandingProvider,
  CurrencyFormatContext,
  useBrandingContext,
} from "@utilifeed/core";
import useAuthStore from "@utilifeed/core/stores/auth.store";
import { PropAIApi } from "@core/apis/PropAI";
import { WeatherApi } from "@core/apis/Weather";
import { CONSUMER_CURRENCY_CONFIG } from "@core/utils";

import organizationStore from "./lib/stores/organization.store";

import "./lib/propai-i18n";

import { GMAPS_KEY, PLAYWRIGHT_RUN, POSTHOG_HOST, POSTHOG_PROPAI_API_KEY } from "@utilifeed/config";
import type { BrandingConfig } from "@utilifeed/config/branding";

import { resolveFontFamily } from "./fonts/font-registry";
import { configureHighchartsDefaults } from "./lib/charts";
import NotFound from "./lib/components/NotFound";
import { ServiceWorker } from "./lib/components/ServiceWorker";
import { routeTree } from "./routeTree.gen";

const domain = import.meta.env.REACT_APP_PROPAI_AUTH0_DOMAIN;
const clientId = import.meta.env.REACT_APP_PROPAI_AUTH0_CLIENTID;
const audience = import.meta.env.REACT_APP_AUTH0_AUDIENCE;

Highcharts.setOptions({
  time: {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    timezone: DateTime.local().zoneName!,
    useUTC: false, // if you enable it, there are no daylight time saving changes.
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry(_, error) {
        return (error as APIError)?.status >= 500;
      },
    },
  },
});

// Define the context type for the router
export interface RouterContext {
  auth: Auth0ContextInterface<User> | undefined;
  apis: {
    propAiApi: PropAIApi | null;
    weatherApi: WeatherApi | null;
  };
  queryClient: QueryClient;
  branding?: BrandingConfig;
}

// Create a new router instance with typed context
const router = createRouter({
  routeTree,
  defaultPendingComponent: () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <CircularProgress />
    </Box>
  ),
  defaultErrorComponent: ({ error }) => (
    // eslint-disable-next-line i18next/no-literal-string
    <div className={`p-2 text-2xl`}>Error: {error.message}</div>
  ),
  defaultNotFoundComponent: () => <NotFound />,
  context: {
    auth: undefined,
    apis: {
      propAiApi: null,
      weatherApi: null,
    },
    queryClient,
  } satisfies RouterContext, // Ensure initial context satisfies the type
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

// Simple mock Auth0 context for Playwright runs (avoids hitting Auth0 during E2E)
const mockAuthContext = {
  isAuthenticated: true,
  isLoading: false,
  user: {
    sub: "playwright-user",
    email: "playwright@example.com",
    name: "Playwright User",
  } as User,
  loginWithRedirect: async () => undefined,
  loginWithPopup: async () => undefined,
  logout: async () => undefined,
  getAccessTokenSilently: async () => "mock-token",
  getAccessTokenWithPopup: async () => "mock-token",
  getIdTokenClaims: async () => ({}) as any,
  buildAuthorizeUrl: async () => "",
  buildLogoutUrl: async () => "",
  handleRedirectCallback: async () => ({ appState: undefined }),
  checkSession: async () => undefined,
  error: undefined,
} as unknown as Auth0ContextInterface<User>;

// Register the router instance and context type for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
    context: RouterContext;
  }
  interface StaticDataRouteOption {
    pageTitle?: string;
    pageIcon?: IconDefinition;
  }
}

// Initialize PostHog (skip when no key or in Playwright runs to avoid noisy warnings)
if (typeof globalThis.window !== "undefined" && POSTHOG_PROPAI_API_KEY && !PLAYWRIGHT_RUN) {
  posthog.init(POSTHOG_PROPAI_API_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
  });
}

// Keep Highcharts defaults in sync with the active branded theme.
const HighchartsThemeSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useBrandingContext();

  useEffect(() => {
    configureHighchartsDefaults(theme);
  }, [theme]);

  return <>{children}</>;
};

// Component that consumes BrandingContext and injects branding into router context
const RouterProviderWithBranding: React.FC<{
  auth: Auth0ContextInterface<User>;
  queryClient: QueryClient;
  propAiApi: PropAIApi | null;
  weatherApi: WeatherApi | null;
}> = ({ auth, queryClient, propAiApi, weatherApi }) => {
  const { branding } = useBrandingContext();

  const mergedContext = {
    auth,
    queryClient,
    apis: {
      propAiApi,
      weatherApi,
    },
    branding,
  };
  return <RouterProvider router={router} context={mergedContext} />;
};

const RouterComponent = () => {
  const auth = useAuth0();
  const orgBranding = organizationStore((s) => s.branding);
  const [tokenManagerReady, setTokenManagerReady] = React.useState(false);
  const [propAiApi, setPropAiApi] = useState<PropAIApi | any>(null);
  const [weatherApi, setWeatherApi] = useState<WeatherApi | null>(null);
  const stubOrganization = useMemo(
    () => ({
      uid: "org-1",
      name: "Demo Organization",
      external_id: "org-1",
      is_admin: false,
    }),
    []
  );

  useEffect(() => {
    if (PLAYWRIGHT_RUN) {
      setPropAiApi({
        getOrganizationList: async () => [stubOrganization],
        getSubstationByOrganization: async () => [
          { uid: "sub-1", name: "Demo Substation", displayName: "Demo Substation" },
        ],
        getNetworks: async () => [],
        getSensorsBySubstation: async () => [],
      });
      setWeatherApi(null);
      organizationStore.setState({ selectedOrganization: stubOrganization, branding: null });
      setTokenManagerReady(true);
      return;
    }

    if (!auth.isLoading && !tokenManagerReady) {
      setTokenManagerReady(true);
    }
  }, [auth, auth.isLoading, tokenManagerReady, stubOrganization]);

  useEffect(() => {
    // Only proceed if auth is authenticated and not loading
    if (PLAYWRIGHT_RUN) return;
    if (auth.isLoading || !auth.isAuthenticated) return;

    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Get token first to ensure auth is fully ready
        const token = await auth.getAccessTokenSilently();
        if (!isMounted) return;

        if (token) {
          // Set the token in the auth store
          useAuthStore.store.setState({ token });

          // Then create APIs with the token getter
          setPropAiApi(new PropAIApi(auth.getAccessTokenSilently));
          setWeatherApi(new WeatherApi(auth.getAccessTokenSilently));

          // Finally identify user in PostHog
          const auth0User = auth.user;
          if (auth0User) {
            posthog.identify(auth0User.sub, {
              email: auth0User.email,
              name: auth0User.name || auth0User.nickname,
            });
          }
        }
      } catch (e) {
        const err = e as {
          error?: string;
          error_description?: string;
          message?: string;
        };
        // eslint-disable-next-line no-console
        console.error("[auth] initializeAuth failed", {
          error: err.error,
          error_description: err.error_description,
          message: err.message,
          raw: e,
        });

        const interactiveErrorCodes = new Set([
          "login_required",
          "consent_required",
          "interaction_required",
          "mfa_required",
        ]);
        if (err.error && interactiveErrorCodes.has(err.error) && isMounted) {
          void auth.loginWithRedirect({
            appState: {
              returnTo: window.location.pathname + window.location.search,
            },
          });
        }
      }
    };

    void initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [auth]);

  // Block rendering until both Auth0 and tokenManager are ready (unless in Playwright mode)
  const isLoginPage = window.location.pathname === "/login";
  if (!PLAYWRIGHT_RUN && !isLoginPage && (auth.isLoading || !tokenManagerReady)) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ApiContext.Provider
      value={{ dataApi: propAiApi, weatherApi: weatherApi, optimizationApi: null }}
    >
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <StyledEngineProvider injectFirst>
          <APIProvider apiKey={GMAPS_KEY}>
            <BrandingProvider branding={orgBranding} fontFamilyResolver={resolveFontFamily}>
              <BrandedThemeProvider>
                <HighchartsThemeSync>
                  <RouterProviderWithBranding
                    auth={auth}
                    queryClient={queryClient}
                    propAiApi={propAiApi}
                    weatherApi={weatherApi}
                  />
                </HighchartsThemeSync>
              </BrandedThemeProvider>
            </BrandingProvider>
          </APIProvider>
        </StyledEngineProvider>
      </LocalizationProvider>
    </ApiContext.Provider>
  );
};

export function App() {
  const onRedirectCallback = async (appState: AppState | undefined) => {
    let navigationParams: {
      to: string;
      params?: Record<string, string>;
      search?: Record<string, unknown>;
      replace?: boolean;
    } = {
      to: "/app",
      replace: true, // Usually replace after login/callback
    };

    if (!appState) {
      await router.navigate({
        ...navigationParams,
        search: navigationParams.search,
      });
      return;
    }

    // Handle activation code route
    if (appState.returnTo && appState.returnTo.includes("activate-account")) {
      const activationCode = appState.returnTo.split("/").pop();
      if (activationCode) {
        // Check if activationCode exists
        navigationParams = {
          ...navigationParams,
          to: "/app/activate-account/$activationCode",
          params: {
            // Correctly place path parameter
            activationCode: activationCode,
          },
        };
      }
    }

    // Perform the navigation
    await router.navigate({
      ...navigationParams,
      search: navigationParams.search,
    });
  };

  const authProvider = useMemo(() => {
    if (PLAYWRIGHT_RUN) {
      return ({ children }: { children: React.ReactNode }) => (
        <Auth0Context.Provider value={mockAuthContext}>{children}</Auth0Context.Provider>
      );
    }

    return ({ children }: { children: React.ReactNode }) => (
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        cacheLocation="localstorage"
        useRefreshTokens
        useRefreshTokensFallback
        authorizeTimeoutInSeconds={10}
        authorizationParams={{
          redirect_uri: globalThis.window.location.origin,
          audience,
        }}
        onRedirectCallback={onRedirectCallback}
      >
        {children}
      </Auth0Provider>
    );
  }, []);

  const AuthProviderComponent = authProvider;

  return (
    <AuthProviderComponent>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <DialogProvider>
            <CurrencyFormatContext.Provider value={CONSUMER_CURRENCY_CONFIG}>
              <ServiceWorker />
              <RouterComponent />
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
              <ToastContainer
                position="bottom-center"
                autoClose={5000}
                limit={2}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </CurrencyFormatContext.Provider>
          </DialogProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </AuthProviderComponent>
  );
}
